<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useEmployeeStore } from '@/stores/employee.js';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';
import Avatar from 'primevue/avatar';
import InputMask from 'primevue/inputmask';

const props = defineProps({
  visible: Boolean,
  employee: Object
});
const emit = defineEmits(['update:visible', 'close', 'save']);

const employeeStore = useEmployeeStore();
const departments = computed(() => employeeStore.allDepartments);
const allPositions = computed(() => employeeStore.allPositions);

const filteredPositions = computed(() => {
  const selectedDeptId = form.value.department;
  if (!selectedDeptId) {
    return allPositions.value.filter(p => p.department_id === null);
  }
  return allPositions.value.filter(p => p.department_id === selectedDeptId || p.department_id === null);
});

const emptyForm = {
  first_name: '', last_name: '', email: '', department: null, post: null,
  birth_date: null, mobile_phone: '', work_phone: '', qualification: '',
  additional_education: '', avatar: null,
};

const form = ref({ ...emptyForm });
const photoFile = ref(null);
const fileUploaderRef = ref(null);

const initials = computed(() => {
  const firstName = form.value.first_name || '';
  const lastName = form.value.last_name || '';
  return ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || '?';
});

const apiOrigin = computed(() => new URL(import.meta.env.VITE_API_BASE_URL, window.location.origin).origin);
const fileUrl = (path) => {
  if (!path) return null;
  // Если URL уже абсолютный, возвращаем его как есть
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  // Иначе, собираем полный URL
  return `${apiOrigin.value}${path.startsWith('/') ? '' : '/'}${path}`;
};

watch(() => props.visible, async (isVisible) => {
  if (isVisible) {
    photoFile.value = null;

    await Promise.all([
      employeeStore.fetchAllDepartments(),
      employeeStore.fetchAllPositions(),
    ]);


    if (props.employee) {

      const employeeData = JSON.parse(JSON.stringify(props.employee));
      const departmentObj = departments.value.find(d => d.name === employeeData.department);
      const departmentId = departmentObj ? departmentObj.id : null;


      // 1. Устанавливаем все, КРОМЕ должности, чтобы запустить фильтрацию
      form.value = { ...emptyForm, ...employeeData, department: departmentId, post: null };

      // 2. Ждем, пока Vue обновит DOM и опции в селекте
      await nextTick();

      // 3. Теперь устанавливаем должность
      form.value.post = employeeData.post_id;

    } else {
      form.value = { ...emptyForm };
    }
  }
}, { immediate: true });

watch(() => form.value.department, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    form.value.post = null;
  }
});

function onFileChange(event) {
  if (event.target.files && event.target.files[0]) {
    const file = event.target.files[0];
    file.objectURL = URL.createObjectURL(file);
    photoFile.value = file;
  }
}

function close() { emit('update:visible', false); emit('close'); }

function submit() {
  const formData = new FormData();
  Object.keys(form.value).forEach(key => {
    if (key === 'avatar' || key === 'post_id' || !form.value[key]) return;
    let value = form.value[key];
    if (key === 'birth_date' && value) {
      const d = new Date(value);
      value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    formData.append(key, value);
  });
  if (photoFile.value) formData.append('photo', photoFile.value);
  emit('save', { id: props.employee?.id, data: formData });
  close();
}
</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="close"
      modal
      :header="employee ? 'Редактировать сотрудника' : 'Добавить сотрудника'"
      :style="{ width: '600px' }"
      position="top"
      :closable="true"
  >
    <form @submit.prevent="submit" class="flex flex-col gap-8 p-fluid">
      <div class="flex gap-6 items-center">
        <div class="flex-shrink-0">
          <Avatar
              v-if="photoFile || fileUrl(form.avatar)"
              :image="photoFile ? photoFile.objectURL : fileUrl(form.avatar)"
              size="xlarge"
              shape="circle"
              class="cursor-pointer"
              @click="() => fileUploaderRef.click()"
          />
          <Avatar
              v-else
              :label="initials"
              size="xlarge"
              shape="circle"
              class="cursor-pointer"
              style="background-color: #9ca3af; color: white;"
              @click="() => fileUploaderRef.click()"
          />
          <input type="file" ref="fileUploaderRef" @change="onFileChange" accept="image/*" class="hidden" />
        </div>
        <div class="flex-grow flex flex-col gap-6 pt-4">
          <FloatLabel variant="on">
            <InputText id="last_name" v-model="form.last_name" required autocomplete="off" />
            <label for="last_name">Фамилия</label>
          </FloatLabel>
          <FloatLabel variant="on">
            <InputText id="first_name" v-model="form.first_name" required autocomplete="off" />
            <label for="first_name">Имя</label>
          </FloatLabel>
        </div>
      </div>

      <div class="flex flex-wrap justify-between gap-y-8 gap-x-4">
        <div class="flex-grow basis-[45%]">
          <FloatLabel variant="on">
            <InputMask id="mobile_phone" v-model="form.mobile_phone" mask="+7(999)999-9999" placeholder="+7(123)456-7890" :autoClear="false" class="w-full" />
            <label for="mobile_phone">Мобильный телефон</label>
          </FloatLabel>
        </div>
        <div class="flex-grow basis-[45%]">
          <FloatLabel variant="on">
            <InputMask id="work_phone" v-model="form.work_phone" mask="+7(999)999-9999" placeholder="+7(123)456-7890" :autoClear="false" class="w-full" />
            <label for="work_phone">Рабочий телефон</label>
          </FloatLabel>
        </div>
        <div class="flex-grow basis-[45%]">
          <FloatLabel variant="on">
            <InputText id="email" v-model="form.email" type="email" autocomplete="off" class="w-full" />
            <label for="email">Email</label>
          </FloatLabel>
        </div>
        <div class="flex-grow basis-[45%]">
          <FloatLabel variant="on">
            <DatePicker id="birth_date" v-model="form.birth_date" dateFormat="yy-mm-dd" showIcon class="w-full" />
            <label for="birth_date">Дата рождения</label>
          </FloatLabel>
        </div>
      </div>

      <div class="flex flex-col gap-8">
        <FloatLabel variant="on">
          <Select
              id="department" v-model="form.department" :options="departments"
              optionLabel="name" optionValue="id" class="w-full"
          />
          <label for="department">Департамент</label>
        </FloatLabel>
        <FloatLabel variant="on">
          <Select
              id="post" v-model="form.post" :options="filteredPositions"
              optionLabel="name" optionValue="id"
              :disabled="filteredPositions.length === 0 && !form.post"
              class="w-full" required
          />
          <label for="post">Должность</label>
        </FloatLabel>
      </div>

      <div class="flex flex-col gap-8 w-full">
        <FloatLabel variant="on">
          <InputText id="qualification" v-model="form.qualification" autocomplete="off" class="w-full" />
          <label for="qualification">Образование</label>
        </FloatLabel>
        <FloatLabel variant="on">
          <InputText id="additional_education" v-model="form.additional_education" autocomplete="off" class="w-full" />
          <label for="additional_education">Квалификация</label>
        </FloatLabel>
      </div>

      <div class="flex justify-center gap-4 pt-2">
        <Button :label="employee ? 'Сохранить' : 'Добавить'" type="submit" severity="info" rounded />
        <Button label="Отмена" type="button" severity="secondary" rounded @click="close" />
      </div>
    </form>
  </Dialog>
</template>